/**
 * TK-38 việc 2c/2d (lee 23/10) — NÚT NGÕ RA THEO TUYẾN + KHUNG TUYẾN ĐANG CHỌN ở trang Vận hành.
 *   · `NutNgoTuyen` (đầu danh sách tuyến): MỘT nút, lấy trạng thái của tuyến đang chọn — màu = trạng thái TỦ BÁO (máy chủ tổng hợp,
 *     `ngo` của /api/tree): xanh lá bật hết · xám tắt hết · cam một phần · viền xám đứt = chưa rõ. Bên cạnh: thông tin đại diện
 *     (tên tuyến · x/y tủ đang bật · chưa rõ z tủ). Bấm ⇒ hộp hỏi "Bật/Tắt mọi ngõ của N tủ …?" ⇒ `POST /api/routes/:id/control`.
 *     **Trả về tự động** (`/api/routes/:id/release`) luôn đi kèm — luật trả quyền; cũng hỏi xác nhận (đụng điện cả tuyến).
 *   · `KhungTuyen` (cột phải dưới bản đồ): số đại diện của tuyến · danh sách tủ của tuyến (bấm ⇒ chọn tủ). TK-46 §1: "Dim cả tuyến"
 *     chuyển vào khối "Điều khiển độ sáng" (`KhoiDieuKhien`, phạm vi Tuyến) — một thao tác một chỗ.
 *   · `KhungToanBo`: chưa chọn tuyến ⇒ số toàn hệ thống (đèn theo trạng thái), không nút điều khiển.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Power } from 'lucide-react';
import { Button, ConfirmDialog, Modal, useToast } from '../ui';
import { KhoaTheoTuyen } from '../Khoa';
import { api, errorMessage } from '../../lib/api';
import type { QuyetDinhCoTuyen } from '../../lib/quyen';
import { useAuth } from '../../lib/auth';
import type { RouteControlResult, TreeRoute } from '../../lib/types';
import { MAU_NGO, khoaNgo, xepTu, type NgoTuyen } from '../../lib/vanHanh';
import { MAU_BADGE, NHAN_BADGE, chuLyDo, type BadgeDen, type LyDoKhongNhan } from '../../lib/nemaNhan';
import type { DenBanDo } from '../nema/LopDenNema';

type Qd = QuyetDinhCoTuyen | 'khong-co-tuyen' | 'chua-biet';
type R = TreeRoute & { ngo?: NgoTuyen | null };
const MOI_NGO = [1, 2, 3];
/* màu của CẢ nút theo trạng thái (token): xanh lá chỉ khi bật hết; mất kết nối / chưa rõ = xám */
const LOP_NUT: Record<ReturnType<typeof khoaNgo>, string> = {
  bat: 'border-tot/70 bg-tot/15 text-tot',
  tat: 'border-chu-3/70 bg-chu-3/15 text-chu-2',
  'mot-phan': 'border-canh/70 bg-canh/15 text-canh',
  'chua-ro': 'border-dashed border-chu-3 bg-transparent text-chu-3',
};

/** Thông tin đại diện của tuyến cạnh nút: "x/y tủ đang bật · chưa rõ z tủ". */
export function daiDienTuyen(n: NgoTuyen | null | undefined): { chinh: string; phu: string | null } {
  if (!n) return { chinh: '—', phu: null };
  return { chinh: n.tuNoi > 0 ? `${n.tuBat}/${n.tuNoi} tủ đang bật` : '—', phu: n.tuMat > 0 ? `chưa rõ ${n.tuMat} tủ` : null };
}

export function NutNgoTuyen({ r }: { r: R | null }) {
  if (!r) {
    return (
      <div className="flex items-center gap-2.5" data-o="nut-ngo-tuyen" data-trang-thai="chua-chon">
        <button type="button" disabled className={`inline-flex min-h-[44px] shrink-0 cursor-not-allowed items-center gap-2 rounded-nut border px-3 text-sm font-semibold ${LOP_NUT['chua-ro']}`}>
          <Power className="h-4 w-4" aria-hidden="true" />Bật/Tắt
        </button>
        <span className="text-sm text-chu-3">Chọn tuyến</span>
      </div>
    );
  }
  return <NutNgoCoTuyen key={r.id} r={r} />;
}

function NutNgoCoTuyen({ r }: { r: R }) {
  const qd: Qd = useAuth().coCoTuyen('canControl', r.id);
  const [hoi, setHoi] = useState(false);
  const [hoiTraVe, setHoiTraVe] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const k = khoaNgo(r.ngo);
  const soTu = r.cabinets.length;
  const gui = useMutation({
    mutationFn: (on: boolean) => api.post<RouteControlResult>(`/api/routes/${r.id}/control`, { outputs: MOI_NGO.map((index) => ({ index, on })) }),
    onSuccess: (kq, on) => {
      setHoi(false);
      toast.success(`${on ? 'Bật' : 'Tắt'} ${r.tenDayDu}: ${kq.sent}/${kq.total} tủ đã gửi${kq.offline ? ` · ${kq.offline} tủ mất kết nối` : ''}`);
      void qc.invalidateQueries({ queryKey: ['tree'] });
    },
    onError: (e) => { setHoi(false); toast.error('Không gửi được lệnh tuyến', errorMessage(e)); },
  });
  const traVe = useMutation({
    mutationFn: () => api.post<RouteControlResult>(`/api/routes/${r.id}/release`, {}),
    onSuccess: (kq) => { setHoiTraVe(false); toast.success(`${r.tenDayDu}: ${kq.sent}/${kq.total} tủ về tự động`); void qc.invalidateQueries({ queryKey: ['tree'] }); },
    onError: (e) => { setHoiTraVe(false); toast.error('Không trả về tự động được', errorMessage(e)); },
  });
  const dd = daiDienTuyen(r.ngo);
  /* vòng 2 (mục 7): không tủ nào trực tuyến ⇒ không có ai nhận lệnh ⇒ khoá (không gửi một lệnh vào khoảng không) */
  const khongTuNhan = r.onlineCount === 0;
  const khoa = qd !== 'cho-qua' || soTu === 0 || khongTuNhan;
  return (
    <KhoaTheoTuyen qd={qd} co="canControl" hienHuyHieu={false} trungTinh className="w-full">
      <div className="flex min-w-0 flex-col gap-2" data-o="nut-ngo-tuyen" data-trang-thai={k} data-tuyen-nut={r.id}>
        <div className="flex min-w-0 items-center gap-2.5">
          <button type="button" disabled={khoa} onClick={() => setHoi(true)} aria-label={`Bật/Tắt tuyến ${r.tenDayDu} — ${MAU_NGO[k].nhan}`} title={khongTuNhan ? 'Không tủ nào trực tuyến' : undefined}
            className={`inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-nut border px-3 text-sm font-semibold hover:brightness-125 disabled:cursor-not-allowed disabled:opacity-60 ${LOP_NUT[k]}`}>
            <span className={`h-3 w-3 rounded-full ${MAU_NGO[k].cham}`} aria-hidden="true" />
            <Power className="h-4 w-4" aria-hidden="true" />{MAU_NGO[k].nhan}
          </button>
          <span className="min-w-0 text-sm leading-tight" data-o="dai-dien-tuyen">
            <span className="block truncate font-medium text-chu-1" title={r.tenDayDu}>{r.tenDayDu}</span>
            <span className="block tabular-nums text-chu-2">{dd.chinh}{dd.phu && <span className="text-chu-3"> · {dd.phu}</span>}</span>
            {khongTuNhan && <span className="block text-xs text-chu-3" data-o="khong-tu-nhan">Không tủ nào trực tuyến</span>}
          </span>
        </div>
        <Button size="sm" variant="secondary" className="min-h-[36px] self-start" disabled={khoa || traVe.isPending} onClick={() => setHoiTraVe(true)} data-o="tra-ve-tu-dong">Trả về tự động</Button>
      </div>
      <Modal open={hoi} onClose={() => setHoi(false)} title={`Bật/Tắt mọi ngõ của ${soTu} tủ trên tuyến ${r.tenDayDu}?`}
        footer={<>
          <Button variant="secondary" onClick={() => setHoi(false)} disabled={gui.isPending}>Huỷ</Button>
          <Button variant={k === 'bat' ? 'primary' : 'secondary'} onClick={() => gui.mutate(false)} loading={gui.isPending}>Tắt mọi ngõ</Button>
          <Button variant={k === 'bat' ? 'secondary' : 'primary'} onClick={() => gui.mutate(true)} loading={gui.isPending}>Bật mọi ngõ</Button>
        </>}>
        <p className="m-0 text-sm text-chu-2">{MAU_NGO[k].nhan} · {dd.chinh}{dd.phu ? ` · ${dd.phu}` : ''}</p>
      </Modal>
      <ConfirmDialog open={hoiTraVe} danger={false} loading={traVe.isPending} title={`Trả về tự động cả tuyến ${r.tenDayDu}?`}
        message={<span className="text-chu-2">{soTu} tủ chạy lại theo lịch tại tủ</span>} confirmText="Trả về tự động"
        onConfirm={() => traVe.mutate()} onCancel={() => setHoiTraVe(false)} />
    </KhoaTheoTuyen>
  );
}

function OSoNho({ nhan, gia, xam = false }: { nhan: string; gia: string; xam?: boolean }) {
  return (
    <div className="min-w-0 rounded-nut border border-vien bg-nen-2 px-2.5 py-1.5">
      <small className="block text-xs leading-tight text-chu-2">{nhan}</small>
      <b className={`block truncate text-[15px] font-semibold tabular-nums ${xam || gia === '—' ? 'text-chu-3' : 'text-chu-1'}`}>{gia}</b>
    </div>
  );
}

/** Đếm đèn: tổng · sáng · công suất cộng (chỉ đèn có số; không đèn nào có ⇒ null). den null = chưa tải. */
function demDen(den: DenBanDo[] | null) {
  if (!den) return null;
  const coP = den.filter((d) => d.p_w != null);
  return { tong: den.length, sang: den.filter((d) => d.badge === 'SANG').length, p: coP.length ? coP.reduce((n, d) => n + (d.p_w as number), 0) : null };
}

export function KhungTuyen({ r, coNema, soDen, chuaCoViTri, den = null, tuChon = null, onChonTu, tuNema = null }: {
  r: R; coNema: boolean; soDen: number | null; chuaCoViTri: boolean;
  /** TK-44: tủ của tuyến theo máy chủ NEMA — soDen = đèn THUỘC HỆ THỐNG (0 ⇒ "Chưa cài đèn"); lyDoTraVe null = nhận được */
  tuNema?: { id: string; soDen: number; lyDoTraVe?: LyDoKhongNhan | null }[] | null;
  /** đèn của tuyến (cờ NEMA bật, đã tải); null = chưa có */
  den?: DenBanDo[] | null;
  tuChon?: string | null; onChonTu?: (id: string) => void;
}) {
  const k = khoaNgo(r.ngo);
  const d = demDen(coNema ? den : null);
  const dsTu = useMemo(() => xepTu(r.cabinets), [r.cabinets]);
  const denTheoTu = useMemo(() => { const m = new Map<string, number>(); for (const x of den ?? []) m.set(x.cabinetId, (m.get(x.cabinetId) ?? 0) + 1); return m; }, [den]);
  const daCai = useMemo(() => new Map((tuNema ?? []).map((t) => [t.id, t.soDen])), [tuNema]);
  return (
    <section className="flex flex-col gap-2.5 rounded-the border border-vien bg-nen-1 p-3" data-o="khung-tuyen" aria-label={r.tenDayDu}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`h-3 w-3 shrink-0 rounded-full ${MAU_NGO[k].cham}`} title={MAU_NGO[k].nhan} aria-hidden="true" />
        <h2 className="m-0 min-w-0 flex-1 truncate text-base font-semibold text-chu-1" title={r.tenDayDu}>{r.tenDayDu}</h2>
        {chuaCoViTri && <span className="text-xs text-chu-3" data-o="tuyen-chua-vi-tri">Tuyến chưa có vị trí</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-o="so-tuyen">
        <OSoNho nhan="Tủ trực tuyến" gia={`${r.onlineCount}/${r.cabinetCount}`} />
        <OSoNho nhan="Số đèn" gia={d ? String(d.tong) : '—'} />
        <OSoNho nhan="Đèn sáng" gia={d ? String(d.sang) : '—'} />
        <OSoNho nhan="Công suất đèn" gia={d?.p != null ? `${d.p} W` : '—'} />
      </div>
      {dsTu.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1 p-0" data-o="tu-cua-tuyen" aria-label="Tủ của tuyến">
          {dsTu.map((c) => {
            const on = c.status === 'ONLINE';
            const chon = c.id === tuChon;
            const n = denTheoTu.get(c.id) ?? 0;
            return (
              <li key={c.id}>
                <button type="button" onClick={() => onChonTu?.(c.id)} aria-pressed={chon} data-tu-dong={c.id}
                  className={`flex min-h-[40px] w-full min-w-0 items-center gap-2.5 rounded-nut border px-2.5 py-1.5 text-left text-sm hover:bg-nen-2 ${chon ? 'border-nhan bg-nen-2' : 'border-vien'}`}>
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${on ? 'bg-tot' : 'bg-chu-3'}`} aria-hidden="true" />
                  <span className={`min-w-0 flex-1 truncate font-medium ${on ? 'text-chu-1' : 'text-chu-3'}`}>{c.name}</span>
                  <span className={`shrink-0 text-xs ${on ? 'text-chu-2' : 'text-chu-3'}`}>{on ? 'Trực tuyến' : 'Mất kết nối'}</span>
                  {/* TK-44: tủ chưa có bảng đèn đã lưu ⇒ "Chưa cài đèn" (không phải "0 đèn") */}
                  {coNema && <span className="shrink-0 text-right text-xs tabular-nums text-chu-2" data-chua-cai={daCai.get(c.id) === 0 ? '1' : undefined}>{!den ? '—' : daCai.get(c.id) === 0 ? 'Chưa cài đèn' : `${n} đèn`}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Chưa chọn tuyến: số toàn hệ thống — đèn theo trạng thái (chỉ trạng thái đang có), công suất đèn cộng. */
export function KhungToanBo({ cay, coNema, den }: { cay: R[] | null; coNema: boolean; den: DenBanDo[] | null }) {
  const d = demDen(coNema ? den : null);
  const theoBadge = useMemo(() => { const m = new Map<BadgeDen, number>(); for (const x of den ?? []) m.set(x.badge, (m.get(x.badge) ?? 0) + 1); return m; }, [den]);
  /* vòng 2 (mục 1): cây chưa tải / lỗi API ⇒ "—" (luật số 0), không đếm ra 0 từ danh sách rỗng */
  const dem = (t: 'bat' | 'tat' | 'mot-phan') => (cay ? String(cay.filter((r) => r.ngo?.trangThai === t).length) : '—');
  return (
    <section className="flex flex-col gap-2.5 rounded-the border border-vien bg-nen-1 p-3" data-o="khung-toan-bo" aria-label="Tất cả tuyến">
      <h2 className="m-0 text-base font-semibold text-chu-1">Tất cả tuyến</h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <OSoNho nhan="Tuyến bật hết" gia={dem('bat')} />
        <OSoNho nhan="Bật một phần" gia={dem('mot-phan')} />
        <OSoNho nhan="Đèn sáng" gia={d && d.tong > 0 ? `${d.sang}/${d.tong}` : '—'} />
        <OSoNho nhan="Công suất đèn" gia={d?.p != null ? `${d.p} W` : '—'} />
      </div>
      {coNema && theoBadge.size > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1.5 p-0 text-sm text-chu-2" data-o="den-theo-trang-thai">
          {(Object.keys(NHAN_BADGE) as BadgeDen[]).filter((b) => (theoBadge.get(b) ?? 0) > 0).map((b) => (
            <li key={b} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: MAU_BADGE[b] }} />{NHAN_BADGE[b].nhan} <b className="tabular-nums text-chu-1">{theoBadge.get(b)}</b></li>
          ))}
        </ul>
      )}
    </section>
  );
}
